import { isTauri } from '../utils/tauri';
import { sanitizeRichTextHtml } from '../utils/sanitizeHtml';
import { parseEmbedSrc, videoEmbedWatchUrl } from '../utils/videoEmbeds';
import { resolveExportBodyHtml } from '../utils/resolveDocMediaForExport';
import { saveTextFile } from '../utils/saveTextFile';
import { calloutToMarkdown } from '../utils/docMarkdown/callout';
import { docExportCss } from './document-editor/docTypography';

function slugify(name) {
  return (
    (name || 'document')
      .trim()
      .replace(/[^\w.\- ]+/g, '')
      .replace(/\s+/g, '-')
      .replace(/^-+|-+$/g, '') || 'document'
  );
}

/**
 * Binary sibling of `saveTextFile`. Uses `writeFile`, which needs the
 * `fs:allow-write-file` capability — the same grant chat attachment downloads
 * turned out to be missing, and for the same reason: text and binary are
 * separate fs commands with separate permissions.
 */
export async function saveBinaryFile({ bytes, filename, mime, filters }) {
  if (isTauri()) {
    const { save } = await import('@tauri-apps/plugin-dialog');
    const { writeFile } = await import('@tauri-apps/plugin-fs');
    const filePath = await save({ defaultPath: filename, title: 'Export document', filters });
    if (filePath == null) return { saved: false, cancelled: true };
    await writeFile(filePath, bytes);
    return { saved: true, path: filePath };
  }
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { saved: true };
}

// ── HTML → Markdown (covers what the Lexical editor produces) ─────────────────

/**
 * An encrypted document image's markdown stand-in, or '' for anything this
 * export has no text for. Markdown is a TEXT format: documentToMarkdown does
 * NOT fetch or decrypt — a data: blob pasted into a .md file is noise, and an
 * <img src=<bucket-url>> would be a link to ciphertext no reader can render
 * (and shipping `data-enc-key` beside it is the key leak the exports exist to
 * avoid). So the image becomes a named placeholder, and a pending image
 * (never uploaded — nothing exists to name) becomes nothing.
 * data: images keep their historical behavior here: skipped.
 */
function imageMd(node) {
  if (!node.hasAttribute('data-enc-key')) return '';
  if (node.hasAttribute('data-pending-ref')) return '';
  const name = node.getAttribute('data-name') || node.getAttribute('alt') || 'image';
  return `[Image: ${name}]`;
}

/** A video embed iframe -> a markdown watch link, or '' when the src does not
 *  parse (the sanitizer already killed those; this holds independently). */
function videoMd(node) {
  const parsed = parseEmbedSrc(node.getAttribute('src') || '');
  const watchUrl = parsed ? videoEmbedWatchUrl(parsed) : null;
  return watchUrl ? `[Watch video](${watchUrl})` : '';
}

// Lexical saves bold as <b><strong>…</strong></b> and italic as <i><em>…</em></i>:
// the inner tag is the SAME format, not a second one. Wrapping both turned
// italic into "**it**" (bold) and bold into "****bold****" (02.10.2026).
const SAME_FORMAT = { b: 'bold', strong: 'bold', i: 'italic', em: 'italic', s: 'strike', del: 'strike', strike: 'strike' };
const alreadyWrapped = (node, tag) =>
  Boolean(node.parentElement && SAME_FORMAT[node.parentElement.tagName.toLowerCase()] === SAME_FORMAT[tag]);

function inlineMd(node) {
  if (node.nodeType === 3) return node.textContent; // text
  if (node.nodeType !== 1) return '';
  const tag = node.tagName.toLowerCase();
  const inner = Array.from(node.childNodes).map(inlineMd).join('');
  if (SAME_FORMAT[tag] && alreadyWrapped(node, tag)) return inner;
  switch (tag) {
    case 'strong':
    case 'b':
      return `**${inner}**`;
    case 'em':
    case 'i':
      return `*${inner}*`;
    case 's':
    case 'del':
    case 'strike':
      return `~~${inner}~~`;
    case 'code':
      return '`' + node.textContent + '`';
    case 'a':
      // Page-link chips (data-doc-link) are app-internal — there is no URL a
      // markdown reader could follow, so emit the plain title, never `[t]()`.
      if (node.hasAttribute('data-doc-link')) return inner || node.textContent;
      return `[${inner}](${node.getAttribute('href') || ''})`;
    case 'br':
      return '  \n';
    case 'img':
      // An image in INLINE position (pasted mid-paragraph); the canonical
      // block-level shape DocImageNode saves is handled in blocksMd.
      return imageMd(node);
    case 'iframe':
      return videoMd(node);
    default:
      return inner;
  }
}

function listMd(listEl, ordered, depth) {
  const indent = '  '.repeat(depth);
  // To-do lists: the Lexical editor saves `__lexicallisttype="check"` on the
  // <ul> and `aria-checked` on each task <li> (both preserved by the
  // sanitizer). Either signal marks the list — the per-item state always
  // comes from aria-checked.
  const isChecklist =
    !ordered &&
    (listEl.getAttribute('__lexicallisttype') === 'check' ||
      Array.from(listEl.children).some(
        (li) => li.tagName?.toLowerCase() === 'li' && li.hasAttribute('aria-checked')
      ));
  const lines = [];
  let i = 1;
  for (const li of Array.from(listEl.children)) {
    if (li.tagName.toLowerCase() !== 'li') continue;
    // Inline content excluding nested lists.
    const inlineParts = Array.from(li.childNodes)
      .filter((n) => !(n.nodeType === 1 && /^(ul|ol)$/.test(n.tagName.toLowerCase())))
      .map(inlineMd)
      .join('')
      .trim();
    // An ORPHANED WRAPPER <li> in a checklist — no aria-checked, no text, just
    // a nested list (its parent task was deleted). Real task items always
    // carry aria-checked, so emitting a line here would invent a phantom
    // empty '- [ ] ' task above the children. Skip the line, keep the
    // recursion below so the children still export.
    const wrapperOnly =
      isChecklist && !li.hasAttribute('aria-checked') && !inlineParts;
    if (!wrapperOnly) {
      const prefix = ordered
        ? `${i++}. `
        : isChecklist
          ? (li.getAttribute('aria-checked') === 'true' ? '- [x] ' : '- [ ] ')
          : '- ';
      lines.push(indent + prefix + inlineParts);
    }
    for (const sub of Array.from(li.children)) {
      const t = sub.tagName.toLowerCase();
      if (t === 'ul' || t === 'ol') lines.push(listMd(sub, t === 'ol', depth + 1));
    }
  }
  return lines.join('\n');
}

/**
 * A GitHub pipe table. Markdown cells cannot hold blocks, so each cell's
 * content flattens to one line (inlineMd over its children, checklists kept
 * as [x]/[ ] prefixes); pipes in content are escaped. The first row is the
 * header row — markdown requires one — whether or not it used <th>.
 * (Review finding: tables previously flattened to run-on text and an
 * in-table checklist lost its markers entirely.)
 */
function tableCellMd(cell) {
  const parts = [];
  for (const child of Array.from(cell.childNodes)) {
    if (child.nodeType === 3) {
      parts.push(child.textContent);
      continue;
    }
    if (child.nodeType !== 1) continue;
    const tag = child.tagName.toLowerCase();
    if (tag === 'ul' || tag === 'ol') {
      // listMd flattened onto one line — cell content cannot hold newlines.
      parts.push(listMd(child, tag === 'ol', 0).split('\n').map((l) => l.trim()).join(' '));
    } else {
      parts.push(inlineMd(child));
    }
  }
  return parts.join(' ').replace(/\|/g, '\\|').replace(/\s+/g, ' ').trim();
}

function tableMd(tableEl) {
  const rows = Array.from(tableEl.querySelectorAll('tr')).map((tr) =>
    Array.from(tr.children)
      .filter((c) => /^(td|th)$/i.test(c.tagName))
      .map(tableCellMd)
  );
  if (!rows.length) return '';
  const width = Math.max(...rows.map((r) => r.length));
  const pad = (r) => [...r, ...Array(Math.max(0, width - r.length)).fill('')];
  const line = (cells) => `| ${pad(cells).join(' | ')} |`;
  const [head, ...body] = rows;
  return [
    line(head),
    `| ${Array(width).fill('---').join(' | ')} |`,
    ...body.map(line),
  ].join('\n');
}

function blocksMd(root) {
  const out = [];
  for (const child of Array.from(root.childNodes)) {
    if (child.nodeType === 3) {
      const t = child.textContent.trim();
      if (t) out.push(t);
      continue;
    }
    if (child.nodeType !== 1) continue;
    const tag = child.tagName.toLowerCase();
    switch (tag) {
      case 'h1':
        out.push(`# ${inlineMd(child).trim()}`);
        break;
      case 'h2':
        out.push(`## ${inlineMd(child).trim()}`);
        break;
      case 'h3':
        out.push(`### ${inlineMd(child).trim()}`);
        break;
      case 'h4':
        out.push(`#### ${inlineMd(child).trim()}`);
        break;
      case 'blockquote':
        out.push(
          inlineMd(child)
            .trim()
            .split('\n')
            .map((l) => `> ${l}`)
            .join('\n')
        );
        break;
      case 'div':
        // A callout (CalloutNode): GitHub's alert syntax, like git sync writes it.
        if (child.hasAttribute('data-callout')) {
          out.push(calloutToMarkdown(child.getAttribute('data-callout'), inlineMd(child).trim()));
        } else {
          const inner = inlineMd(child).trim();
          if (inner) out.push(inner);
        }
        break;
      case 'pre':
        out.push('```\n' + child.textContent.replace(/\n$/, '') + '\n```');
        break;
      case 'ul':
        out.push(listMd(child, false, 0));
        break;
      case 'table':
        out.push(tableMd(child));
        break;
      case 'ol':
        out.push(listMd(child, true, 0));
        break;
      case 'p': {
        const inner = inlineMd(child).trim();
        out.push(inner); // keep blank paragraphs as spacing
        break;
      }
      case 'img': {
        // Block-level (DocImageNode is not inline): a quoted placeholder
        // line, so the reader sees where the image was and what it was
        // called. Pending and data: images contribute nothing (imageMd).
        const placeholder = imageMd(child);
        if (placeholder) out.push(`> ${placeholder}`);
        break;
      }
      case 'iframe': {
        const link = videoMd(child);
        if (link) out.push(link);
        break;
      }
      default: {
        const inner = inlineMd(child).trim();
        if (inner) out.push(inner);
      }
    }
  }
  return out.join('\n\n').replace(/\n{3,}/g, '\n\n').trim();
}

export function documentToMarkdown(title, bodyHtml) {
  const container = document.createElement('div');
  container.innerHTML = sanitizeRichTextHtml(bodyHtml || '');
  const body = blocksMd(container);
  const heading = title && title.trim() ? `# ${title.trim()}\n\n` : '';
  return `${heading}${body}\n`;
}

export async function documentToHtmlFile(title, bodyHtml) {
  const safeTitle = (title || 'Untitled').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  // Sanitize + resolve: encrypted images become self-contained data: URIs and
  // every data-enc-* attribute is stripped — a standalone .html file travels,
  // and data-enc-key in it would hand the decryption key to anyone holding
  // the file. Video iframes are kept AS-IS: unlike a PDF, a browser renders
  // them fine, and the sanitizer already rebuilt their srcs canonically.
  const body = await resolveExportBodyHtml(bodyHtml);
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${safeTitle}</title>
<style>
  ${docExportCss()}
</style>
</head>
<body>
<h1>${safeTitle}</h1>
${body}
</body>
</html>
`;
}

/**
 * Export the document in the given format.
 * @param {'pdf'|'markdown'|'html'} format
 * @param {{ title?: string, bodyHtml?: string }} doc
 */
/**
 * Save an already-rendered folder PDF.
 *
 * Separate from `exportDocument` because the bytes are produced elsewhere
 * (documentFolderPdf.js reads N documents first), but it shares this module's
 * save dialog so a folder export and a document export put up the same picker
 * with the same filters.
 */
export async function exportFolderPdf({ folderName = 'Documents', bytes } = {}) {
  return saveBinaryFile({
    bytes,
    filename: `${slugify(folderName)}.pdf`,
    mime: 'application/pdf',
    filters: [{ name: 'PDF', extensions: ['pdf'] }],
  });
}

/** Save a folder exported as a zip of Markdown files (the row menu's "Export folder…"). */
export async function exportFolderZip({ folderName = 'Documents', bytes } = {}) {
  return saveBinaryFile({
    bytes,
    filename: `${slugify(folderName)}.zip`,
    mime: 'application/zip',
    filters: [{ name: 'Zip', extensions: ['zip'] }],
  });
}

export async function exportDocument(format, { title = 'Untitled', bodyHtml = '' } = {}) {
  const base = slugify(title);
  if (format === 'pdf') {
    // A REAL FILE, like the two exports below it — not the print dialog. See
    // documentPdf.js for why pdfmake rather than rasterising or the webview's
    // print path.
    const { renderDocumentPdf } = await import('./documentPdf');
    const bytes = await renderDocumentPdf({ title, bodyHtml });
    return saveBinaryFile({
      bytes,
      filename: `${base}.pdf`,
      mime: 'application/pdf',
      filters: [{ name: 'PDF', extensions: ['pdf'] }],
    });
  }
  if (format === 'html') {
    return saveTextFile({
      content: await documentToHtmlFile(title, bodyHtml),
      filename: `${base}.html`,
      mime: 'text/html',
      filters: [{ name: 'HTML', extensions: ['html', 'htm'] }],
    });
  }
  if (format === 'markdown') {
    return saveTextFile({
      content: documentToMarkdown(title, bodyHtml),
      filename: `${base}.md`,
      mime: 'text/markdown',
      filters: [{ name: 'Markdown', extensions: ['md', 'markdown'] }],
    });
  }
  return { saved: false };
}
